import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import logo from '../../assets/LOGO.png'
import '../../App.css'
import './header.css'
import { scrollTo } from '../../utils/lenis'

const homeSectionMap = {
    Home: 'home',
    Featured: 'featured',
    'Our Team': 'our-team',
    'Contact Us': 'contact-us'
}

const aboutSectionMap = {
    'About Us': 'about-us',
    'Why Us': 'key-features',
    'Our Team': 'our-team',
    'Our Products': 'our-products'
}

const THRESHOLDS = (() => {
    const t = [];
    for (let i = 0; i <= 1; i += 0.1) t.push(Math.round(i * 10) / 10);
    return t;
})();

const HYSTERESIS = 0.12; // must exceed current section's ratio by 12% before switching

export default function Header() {
    const navigate = useNavigate();
    const location = useLocation();
    const isHome = location.pathname === '/';
    const isProject = location.pathname.startsWith('/project');

    const [activeTab, setActiveTab] = useState(isProject ? 'Our Work' : isHome ? 'Home' : 'About Us');
    const scrollLockRef = useRef(false);
    const scrollGenRef = useRef(0);
    const buttonGroupRef = useRef(null);
    const [indicatorStyle, setIndicatorStyle] = useState({});

    const sectionMap = isHome ? homeSectionMap : aboutSectionMap;
    const navItems = isProject
        ? ['← Back', 'Our Work']
        : isHome
            ? ['Home', 'Featured', 'Our Team', 'Contact Us']
            : ['About Us', 'Why Us', 'Our Team', 'Our Products'];

    const effectiveActiveTab = isProject ? 'Our Work' : activeTab;

    // Reset active tab on route change
    useEffect(() => {
        if (isProject) {
            setActiveTab('Our Work');
            scrollLockRef.current = false;
            return;
        }
        setActiveTab(isHome ? 'Home' : 'About Us');
        scrollLockRef.current = false;
    }, [isHome, isProject]);

    // ── IntersectionObserver-based active section detection ──────────────
    // Replaces the old scroll-based getBoundingClientRect + Lenis listener.
    // Each section is observed with 10 % granularity thresholds within a
    // root-margin'd viewport ( -80px top offset = under the header ).
    // The active tab = the section with the highest intersection ratio.
    // Hysteresis (12 %) prevents rapid switching when two sections have
    // similar visibility at scroll boundaries.
    useEffect(() => {
        if (isProject) return;

        const entries = Object.entries(sectionMap);
        const ratios = new Map();
        let rafPending = false;
        let prevLabel = null;

        const determineActive = () => {
            rafPending = false;
            if (scrollLockRef.current) return;

            // Find the section with the highest visible ratio
            let bestLabel = entries[0][0];
            let bestRatio = -1;

            for (const [label, id] of entries) {
                const ratio = ratios.get(id) ?? 0;
                if (ratio > bestRatio) {
                    bestRatio = ratio;
                    bestLabel = label;
                }
            }

            // Edge case: no section has meaningful visibility in the
            // detection zone (e.g. scrolled past all observed sections
            // into the footer).  Keep the previous active label.
            if (bestRatio < 0.01 && prevLabel !== null) return;

            // Hysteresis — only switch when the candidate section has a
            // clear lead over the current section (prevents ping-pong at
            // boundaries where two sections have near-equal visibility).
            if (prevLabel !== null && bestLabel !== prevLabel) {
                const prevId = sectionMap[prevLabel];
                if (prevId) {
                    const prevRatio = ratios.get(prevId) ?? 0;
                    if (bestRatio - prevRatio < HYSTERESIS) {
                        bestLabel = prevLabel;
                    }
                }
            }

            if (bestLabel !== prevLabel) {
                prevLabel = bestLabel;
                setActiveTab(bestLabel);
            }
        };

        const observer = new IntersectionObserver(
            (obsEntries) => {
                for (const entry of obsEntries) {
                    ratios.set(entry.target.id, entry.intersectionRatio);
                }
                if (!rafPending) {
                    rafPending = true;
                    requestAnimationFrame(determineActive);
                }
            },
            {
                threshold: THRESHOLDS,
                rootMargin: '-80px 0px 0px 0px',
            }
        );

        // Retry observer setup until all sections are found in the DOM.
        // Handles lazy-loaded pages (About Us) where sections mount
        // after the IntersectionObserver effect has already started.
        let retryRaf = null;
        const tryObserve = () => {
            let allFound = true;
            for (const [, id] of entries) {
                const el = document.getElementById(id);
                if (el) {
                    observer.observe(el);
                } else {
                    allFound = false;
                }
            }
            if (!allFound) {
                retryRaf = requestAnimationFrame(tryObserve);
            }
        };
        tryObserve();

        return () => {
            observer.disconnect();
            if (retryRaf) cancelAnimationFrame(retryRaf);
        };
    }, [sectionMap, isProject]);

    // ── Sliding pill indicator — tracks the active button ────────────────
    // Uses getBoundingClientRect once per activeTab change (never during
    // scroll).  The CSS transition on transform + width animates the pill
    // smoothly without triggering layout or paint per frame.
    useLayoutEffect(() => {
        const container = buttonGroupRef.current;
        if (!container) return;
        const activeBtn = container.querySelector('.headerbutton.active');
        if (!activeBtn) return;

        const containerRect = container.getBoundingClientRect();
        const btnRect = activeBtn.getBoundingClientRect();

        setIndicatorStyle({
            transform: `translateX(${btnRect.left - containerRect.left}px)`,
            width: `${btnRect.width}px`,
        });
    }, [effectiveActiveTab, isProject]);

    const executeScroll = (item) => {
        scrollGenRef.current++
        const gen = scrollGenRef.current
        scrollLockRef.current = true
        setActiveTab(item)

        const onComplete = () => {
            if (gen !== scrollGenRef.current) return
            scrollLockRef.current = false
        }

        if (item === 'Home') {
            scrollTo(0, { onComplete })
            return
        }
        if (item === 'Contact Us') {
            const maxScroll = document.documentElement.scrollHeight - window.innerHeight
            scrollTo(maxScroll, { onComplete })
            return
        }
        if (isProject && item === 'Our Work') {
            scrollTo(0, { onComplete })
            return
        }
        const id = sectionMap[item]
        if (id) {
            scrollTo(`#${id}`, { onComplete })
        }
    }

    const handleNavClick = (item) => {
        if (item === '← Back') {
            navigate(-1);
            return;
        }
        executeScroll(item);
    }

    const handleLogoClick = () => {
        if (isProject) {
            navigate('/');
        } else if (!isHome) {
            navigate('/');
        } else {
            executeScroll('Home');
        }
    };

    return (
        <div className='header'>
            <div className='logo' onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
                <img src={logo} alt="logo" className='logoimage' />
                <h1 className="subheading">JOVEXA</h1>
            </div>

            <div className='headerbuttonsection' ref={buttonGroupRef}>
                <div className="header-active-indicator" style={indicatorStyle} aria-hidden="true" />
                {navItems.map((item) => (
                    <button
                        key={item}
                        className={`headerbutton ${effectiveActiveTab === item ? 'active' : ''}`}
                        onClick={() => handleNavClick(item)}
                    >
                        {item}
                    </button>
                ))}
            </div>

            {!isProject && (
                <button className='primarybutton' onClick={() => navigate(isHome ? '/about' : '/')}>
                    {isHome ? 'About us' : 'Home'}
                </button>
            )}
        </div>
    )
}
